import React, { useEffect, useState } from "react";
import { CreditCard, CheckCircle2, AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import api from "@/lib/api";

/**
 * StripePortalReadinessTile — admin tile that pings Stripe for the
 * active customer-portal configuration and flags red if either
 * cancellation OR payment-method updates are turned OFF.
 *
 * Why: our transactional emails and the Employer Dashboard trial
 * banner both route users to the Stripe-hosted portal. If an admin
 * silently flips those toggles OFF in the dashboard, trials start
 * failing to add cards and become support tickets.
 *
 * Platform-admin only. Renders nothing on 403.
 */
export const StripePortalReadinessTile = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = (announce = false) => {
        if (announce) setRefreshing(true);
        api.get("/admin/stripe-portal-readiness")
            .then((r) => setData(r.data))
            .catch(() => { /* 403 hides tile */ })
            .finally(() => { setLoading(false); setRefreshing(false); });
    };

    useEffect(() => { load(); /* eslint-disable-line */ }, []);

    if (loading) {
        return (
            <div className="card-soft p-5" data-testid="portal-readiness-loading">
                <Loader2 size={16} className="animate-spin text-emerald-500" />
            </div>
        );
    }
    if (!data) return null;

    const ok = data.ok;
    const anyWarning = (data.warnings && data.warnings.length > 0);

    const Row = ({ label, enabled, must }) => (
        <li className="flex items-center gap-2 text-sm" data-testid={`portal-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            {enabled ? (
                <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
            ) : (
                <AlertTriangle size={14} className={`${must ? "text-rose-500" : "text-amber-500"} shrink-0`} />
            )}
            <span className={enabled ? "text-foreground" : "text-muted-foreground"}>
                {label}
            </span>
            <span className={`ml-auto text-[10px] font-semibold uppercase tracking-wider ${
                enabled ? "text-emerald-600" : (must ? "text-rose-600" : "text-amber-600")
            }`}>
                {enabled ? "ON" : (must ? "OFF — required" : "OFF")}
            </span>
        </li>
    );

    return (
        <div
            className={`card-soft p-5 border-2 ${ok ? "border-transparent" : "border-rose-500/50"}`}
            data-testid="portal-readiness-tile"
            data-ok={String(ok)}
        >
            <div className="flex items-center gap-2 mb-3">
                <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${
                    ok ? "bg-emerald-500/10 text-emerald-600" : "bg-rose-500/10 text-rose-600"
                }`}>
                    <CreditCard size={16} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Billing portal readiness</div>
                    <div className="text-sm font-heading font-semibold text-foreground flex items-center gap-2">
                        {ok
                            ? <>All toggles on <CheckCircle2 size={14} className="text-emerald-500" /></>
                            : <>Action needed <AlertTriangle size={14} className="text-rose-500" /></>
                        }
                    </div>
                </div>
                <button
                    onClick={() => load(true)}
                    disabled={refreshing}
                    className="rounded-full border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted inline-flex items-center gap-1"
                    data-testid="portal-readiness-refresh"
                    title="Re-check Stripe"
                >
                    {refreshing ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                </button>
            </div>
            {data.configured ? (
                <ul className="space-y-2">
                    <Row label="Cancel subscriptions" enabled={!!data.cancel_enabled} must />
                    <Row label="Update payment methods" enabled={!!data.payment_method_update_enabled} must />
                    <Row label="Invoice history" enabled={!!data.invoice_history_enabled} must={false} />
                </ul>
            ) : (
                <p className="text-sm text-muted-foreground">
                    No active portal configuration found in Stripe.
                </p>
            )}
            {anyWarning && (
                <div className="mt-3 rounded-md border border-rose-500/40 bg-rose-500/5 p-3 text-xs text-rose-800 dark:text-rose-300 space-y-1">
                    {data.warnings.map((w, i) => (
                        <p key={i} className="leading-snug" data-testid={`portal-readiness-warning-${i}`}>• {w}</p>
                    ))}
                </div>
            )}
            {data.configuration_id && (
                <p className="mt-3 text-[10px] text-muted-foreground font-mono truncate" title={data.configuration_id}>
                    cfg {data.configuration_id}
                </p>
            )}
        </div>
    );
};

export default StripePortalReadinessTile;
